/**
 * BrandStep — Onboarding step 2
 *
 * Two internal sub-steps:
 *
 *   1. "lookup"       — Narrow layout (max-w-md). Domain input for Brandfetch lookup
 *                       or a "Set up manually" link to skip the lookup.
 *
 *   2. "preview-edit" — Wide layout (max-w-5xl, side-by-side). Left panel has
 *                       editable logo + primary color. Right panel shows a live
 *                       16:9 CoverSlide preview that reacts to every change.
 *                       "Use this brand" uploads the logo (if changed) then saves
 *                       brand settings. "Try a different domain" goes back to step 1.
 *
 * The wizard's external step counter does NOT change between sub-steps — this is
 * entirely local state. `onSubStepChange(isWide)` is called so WelcomeSequence
 * can widen the OnboardingLayout for sub-step 2.
 *
 * Logo upload pattern (mirrors Settings › Brand tab):
 *   - File is staged as an Object URL for instant preview.
 *   - Uploaded to Spaces via filesApi.upload only when user confirms.
 *   - Object URL is revoked on unmount / reset to avoid memory leaks.
 *
 * Color preview pattern:
 *   - CoverSlidePreview receives `primaryColor` as a hex string.
 *   - It applies brand colors to document-level CSS vars via applyBrandColors,
 *     restoring the original values on unmount.
 */

import { useState, useRef, useCallback, useEffect, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Search, AlertCircle, Check, Palette } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { usersApi, filesApi } from '@/lib/api'
import { BrandLookupEnrichedResult, BrandSettings } from '@/lib/api/client'
import { extractLookupPalette } from '@/lib/theme/brand-lookup'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import { useBrand } from '@/lib/brand-provider'
import { OnboardingStepProps } from '@/types/components/onboarding'
import { BRAND_PRESET_COLORS } from '@/lib/theme/brand-presets'
import { DualLogoPicker } from '@/components/brand/DualLogoPicker'
import { IconLogoPicker } from '@/components/brand/IconLogoPicker'
import { CoverSlidePreview } from './CoverSlidePreview'
import { FadeUp } from '../FadeUp'

// ─── Types ────────────────────────────────────────────────────────────────────

type BrandSubStep = 'lookup' | 'preview-edit'

// ─── Color utilities ──────────────────────────────────────────────────────────

/**
 * Convert an HSL tuple string ("214 86% 34%") to a CSS hex color ("#1a4d8c").
 * Brandfetch returns primaryColor in this format; all downstream color logic
 * (color swatch, input[type=color], CoverSlidePreview) expects hex.
 */
function hslTupleToHex(hsl: string): string {
  const m = hsl.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/)
  if (!m) return '#000000'

  const h = parseFloat(m[1]) / 360
  const s = parseFloat(m[2]) / 100
  const l = parseFloat(m[3]) / 100

  let r: number
  let g: number
  let b: number

  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      let tt = t
      if (tt < 0) tt += 1
      if (tt > 1) tt -= 1
      if (tt < 1 / 6) return p + (q - p) * 6 * tt
      if (tt < 1 / 2) return q
      if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
      return p
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Normalize any brand color string (hex or HSL tuple) to hex.
 * Ensures the editor state is always a valid CSS hex value.
 */
function normalizeToHex(color: string | null): string | null {
  if (!color) return null
  if (color.startsWith('#')) return color
  return hslTupleToHex(color)
}

// ─── Brandfetch lookup hook ───────────────────────────────────────────────────

function useBrandLookup() {
  const { t } = useTranslation('onboarding')
  const [domain, setDomain] = useState('')
  const [loading, setLoading] = useState(false)
  const [brand, setBrand] = useState<BrandLookupEnrichedResult | null>(null)
  const [error, setError] = useState('')

  async function lookup() {
    const trimmed = domain.trim()
    if (!trimmed) return

    setLoading(true)
    setError('')
    try {
      // Use the enriched endpoint (Brandfetch + website crawl + theme
      // derivation) so onboarding detection matches Settings exactly: all
      // three logos, a full seed palette, preset, fonts, and overrides. The
      // crawl is heavier than the basic lookup, so allow a longer timeout.
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('TIMEOUT')), 20_000),
      )
      const result = await Promise.race([usersApi.lookupBrandEnriched(trimmed), timeout])
      setBrand(result)
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'TIMEOUT') {
        setError(t('brand.timeout'))
      } else {
        setError(getApiErrorMessage(err, t('brand.notFound')))
      }
    } finally {
      setLoading(false)
    }
  }

  function reset() {
    setBrand(null)
    setError('')
  }

  return { domain, setDomain, loading, brand, error, lookup, reset }
}

// ─── Sub-step 1: Domain lookup ────────────────────────────────────────────────

interface LookupSubStepProps {
  domain: string
  setDomain: (v: string) => void
  loading: boolean
  error: string
  onLookup: () => void
  onManual: () => void
  onSkip: () => void
}

function LookupSubStep({
  domain,
  setDomain,
  loading,
  error,
  onLookup,
  onManual,
  onSkip,
}: LookupSubStepProps) {
  const { t } = useTranslation(['onboarding', 'common'])
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      onLookup()
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Heading + subtext — delay 0 */}
      <FadeUp delay={0} className="text-center">
        <Heading level={1} size="4xl">
          {t('brand.title')}
        </Heading>
        <Text size="sm" color="muted" className="mt-2">
          {t('brand.subtitle')}
        </Text>
      </FadeUp>

      {/* Domain input row — delay 120ms */}
      <FadeUp delay={120} className="flex flex-col gap-3">
        <div className="flex gap-2">
          <Input
            data-testid="brand-step-domain-input"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('brand.domainPlaceholder')}
            disabled={loading}
            className="flex-1"
          />
          <Button
            data-testid="brand-step-lookup-button"
            onClick={onLookup}
            disabled={loading || !domain.trim()}
            variant="outline"
            size="lg"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span className="ml-1.5">{t('brand.lookUp')}</span>
          </Button>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <Text size="sm" color="destructive">{error}</Text>
          </div>
        )}
      </FadeUp>

      {/* Secondary actions — delay 200ms */}
      <FadeUp delay={200} className="flex flex-col items-center gap-2">
        <Button
          data-testid="brand-step-manual-button"
          variant="ghost"
          size="sm"
          onClick={onManual}
          className="text-muted-foreground"
        >
          {t('brand.manual')}
        </Button>

        <Button
          data-testid="brand-step-skip-button"
          variant="link"
          onClick={onSkip}
          className="text-muted-foreground"
        >
          {t('brand.skipLater')}
        </Button>
      </FadeUp>
    </div>
  )
}

// ─── Sub-step 2: Preview & edit ───────────────────────────────────────────────

interface PreviewEditSubStepProps {
  editedLogoUrl: string | null
  editedLogoUrlOnDark: string | null
  editedIconLogoUrl: string | null
  editedPrimaryColor: string | null
  suggestedColor: string | null
  brandfetchDomain: string | null
  orgName: string
  isSaving: boolean
  logoError: string | null
  onLogoFileChange: (file: File) => void
  onLogoDarkFileChange: (file: File) => void
  onIconFileChange: (file: File) => void
  onColorChange: (hex: string) => void
  onConfirm: () => void
  onTryDifferentDomain: () => void
  onSkip: () => void
}

function PreviewEditSubStep({
  editedLogoUrl,
  editedLogoUrlOnDark,
  editedIconLogoUrl,
  editedPrimaryColor,
  suggestedColor,
  brandfetchDomain,
  orgName,
  isSaving,
  logoError,
  onLogoFileChange,
  onLogoDarkFileChange,
  onIconFileChange,
  onColorChange,
  onConfirm,
  onTryDifferentDomain,
  onSkip,
}: PreviewEditSubStepProps) {
  const { t } = useTranslation(['onboarding', 'common'])
  const canConfirm = Boolean(editedLogoUrl || editedPrimaryColor)
  const [colorPickerOpen, setColorPickerOpen] = useState(false)

  return (
    <div className="flex flex-col items-center gap-6">
      {/* ── Heading ──────────────────────────────────────────────────── */}
      <FadeUp delay={0} className="text-center">
        <Heading level={2} size="2xl">{t('brand.yourBrand')}</Heading>
        {brandfetchDomain && (
          <Text size="sm" color="muted">{brandfetchDomain}</Text>
        )}
      </FadeUp>

      {/* ── Logo + Icon + Color — side by side, equal height ─────────────── */}
      <FadeUp delay={80} className="flex w-full items-stretch gap-4">
        {/* Light + dark logos */}
        <div className="flex w-[40%] shrink-0 flex-col gap-2">
          <Text size="sm" weight="medium">{t('brand.logo')}</Text>
          <DualLogoPicker
            logoUrl={editedLogoUrl}
            logoUrlOnDark={editedLogoUrlOnDark}
            onLogoFileChange={onLogoFileChange}
            onLogoDarkFileChange={onLogoDarkFileChange}
          />
          {logoError && (
            <div className="flex items-center gap-1.5 text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <Text size="xs" color="destructive">{logoError}</Text>
            </div>
          )}
        </div>

        {/* Icon logo */}
        <div className="flex w-[20%] shrink-0 flex-col gap-2">
          <Text size="sm" weight="medium">{t('brand.icon')}</Text>
          <IconLogoPicker
            iconLogoUrl={editedIconLogoUrl}
            onIconFileChange={onIconFileChange}
            disabled={isSaving}
          />
        </div>

        {/* Primary color */}
        <div className="flex flex-1 flex-col gap-2">
          <Text size="sm" weight="medium">{t('brand.primaryColor')}</Text>

          <Popover open={colorPickerOpen} onOpenChange={setColorPickerOpen}>
            <PopoverTrigger asChild>
              <button
                data-testid="brand-step-color-picker"
                type="button"
                disabled={isSaving}
                aria-expanded={colorPickerOpen}
                className="flex w-full flex-1 items-center gap-3 rounded-xl border border-border bg-background p-2 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div
                  className="aspect-square shrink-0 self-stretch rounded-lg border border-white/10 shadow-sm"
                  style={{
                    backgroundColor: editedPrimaryColor
                      ? editedPrimaryColor
                      : 'transparent',
                    background: editedPrimaryColor
                      ? editedPrimaryColor
                      : 'linear-gradient(135deg, hsl(var(--muted)) 50%, hsl(var(--muted-foreground)/0.2) 50%)',
                  }}
                />
                <span className="flex-1 text-sm text-foreground">
                  {editedPrimaryColor ?? t('brand.choose')}
                </span>
                <Palette className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </PopoverTrigger>

            <PopoverContent align="start" className="w-64 p-3">
              {suggestedColor && suggestedColor !== editedPrimaryColor && (
                <div className="mb-3 pb-3 border-b border-border">
                  <Text size="xs" color="muted" className="mb-1.5">{t('brand.brandColor')}</Text>
                  <button
                    type="button"
                    title={t('brand.restoreColor')}
                    onClick={() => {
                      onColorChange(suggestedColor)
                      setColorPickerOpen(false)
                    }}
                    disabled={isSaving}
                    className="flex items-center gap-2 w-full rounded-lg border border-border px-2 py-1.5 hover:bg-muted/50 transition-colors"
                  >
                    <span
                      className="h-6 w-6 shrink-0 rounded-full border border-white/10"
                      style={{ backgroundColor: suggestedColor }}
                    />
                    <Text size="xs">{suggestedColor.toUpperCase()}</Text>
                  </button>
                </div>
              )}
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label={t('brand.suggestedColors')}
              >
                {BRAND_PRESET_COLORS.map((preset) => (
                  <button
                    key={preset.hex}
                    type="button"
                    title={preset.name}
                    aria-pressed={editedPrimaryColor === preset.hex}
                    onClick={() => {
                      onColorChange(preset.hex)
                      setColorPickerOpen(false)
                    }}
                    disabled={isSaving}
                    className={cn(
                      'h-8 w-8 rounded-full border-2 transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      editedPrimaryColor === preset.hex
                        ? 'scale-110 border-foreground'
                        : 'border-transparent hover:scale-110',
                    )}
                    style={{ backgroundColor: preset.hex }}
                  />
                ))}
              </div>

              <div className="mt-3 flex items-center gap-2 border-t border-border pt-2">
                <input
                  data-testid="brand-step-color-custom-input"
                  type="color"
                  value={editedPrimaryColor ?? '#000000'}
                  onChange={(e) => onColorChange(e.target.value)}
                  disabled={isSaving}
                  className="h-8 w-8 shrink-0 cursor-pointer rounded-md border border-border bg-transparent p-0.5"
                  title={t('brand.customColor')}
                />
                <Text size="xs" color="muted">{t('brand.custom')}</Text>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </FadeUp>

      {/* ── Cover slide preview — compact, centered ──────────────────── */}
      <FadeUp delay={160} className="w-full max-w-2xl">
        <div className="overflow-hidden rounded-lg shadow-md ring-1 ring-border/30">
          <div className="flex items-center justify-center border-b border-border/30 bg-muted/40 py-1">
            <span className="select-none text-[10px] uppercase tracking-wide text-muted-foreground/60">
              {t('brand.coverPreview')}
            </span>
          </div>
          <CoverSlidePreview
            logoUrl={editedLogoUrl}
            primaryColor={editedPrimaryColor}
            orgName={orgName}
          />
        </div>
      </FadeUp>

      {/* ── CTA buttons — centered below preview ─────────────────────── */}
      <FadeUp delay={240} className="flex w-full max-w-xs flex-col gap-2">
        <Button
          data-testid="brand-step-use-brand-button"
          variant="cta"
          onClick={onConfirm}
          disabled={isSaving || !canConfirm}
          className="w-full"
        >
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Check className="mr-2 h-4 w-4" />
          )}
          {t('brand.useBrand')}
        </Button>

        <Button
          data-testid="brand-step-try-another-button"
          variant="ghost"
          onClick={onTryDifferentDomain}
          disabled={isSaving}
          className="w-full"
        >
          {t('brand.tryDomain')}
        </Button>

        <Button
          data-testid="brand-step-skip-button"
          variant="link"
          onClick={onSkip}
          disabled={isSaving}
          className="w-full text-muted-foreground"
        >
          {t('brand.skipLater')}
        </Button>
      </FadeUp>
    </div>
  )
}

// ─── Main BrandStep component ─────────────────────────────────────────────────

export const BrandStep = forwardRef<HTMLDivElement, OnboardingStepProps>(
  function BrandStep({ onNext, onSkip, wizardState, onSubStepChange, className }, ref) {
    const { t } = useTranslation('onboarding')
    const { updateBrandSettings } = useBrand()
    const { domain, setDomain, loading, brand, error, lookup, reset } = useBrandLookup()

    const [brandSubStep, setBrandSubStep] = useState<BrandSubStep>('lookup')
    const [editedLogoUrl, setEditedLogoUrl] = useState<string | null>(null)
    const [editedLogoFile, setEditedLogoFile] = useState<File | null>(null)
    const [editedLogoUrlOnDark, setEditedLogoUrlOnDark] = useState<string | null>(null)
    const [editedLogoDarkFile, setEditedLogoDarkFile] = useState<File | null>(null)
    const [editedIconLogoUrl, setEditedIconLogoUrl] = useState<string | null>(null)
    const [editedIconLogoFile, setEditedIconLogoFile] = useState<File | null>(null)
    const [editedPrimaryColor, setEditedPrimaryColor] = useState<string | null>(null)
    const [brandfetchSecondaryColor, setBrandfetchSecondaryColor] = useState<string | null>(null)
    const [brandfetchDomain, setBrandfetchDomain] = useState<string | null>(null)
    // Auto-detected icon (favicon candidate) — saved alongside the light/dark
    // logos so onboarding captures all three assets, matching Settings.
    const [detectedIconLogoUrl, setDetectedIconLogoUrl] = useState<string | null>(null)
    // Full theme suggestion (preset, overrides, fonts, seed palette) captured
    // from the enriched lookup. Persisted on confirm so the live theme switches
    // immediately instead of only saving a bare primary color.
    const [detectedTheme, setDetectedTheme] = useState<BrandLookupEnrichedResult['suggestedTheme'] | null>(null)
    const [detectedBrandThemeConfig, setDetectedBrandThemeConfig] = useState<NonNullable<BrandLookupEnrichedResult['suggestedBrandThemeConfig']> | null>(null)
    const [detectedAccentHex, setDetectedAccentHex] = useState<string | null>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [logoError, setLogoError] = useState<string | null>(null)

    const stagedObjectUrlRef = useRef<string | null>(null)
    const stagedDarkObjectUrlRef = useRef<string | null>(null)
    const savingRef = useRef(false)

    useEffect(() => {
      return () => {
        if (stagedObjectUrlRef.current) {
          URL.revokeObjectURL(stagedObjectUrlRef.current)
        }
        if (stagedDarkObjectUrlRef.current) {
          URL.revokeObjectURL(stagedDarkObjectUrlRef.current)
        }
      }
    }, [])

    /**
     * Advance to preview-edit sub-step, pre-filling from an enriched Brandfetch
     * result or null (manual setup).
     *
     * Mirrors the Settings › Brand detection parsing: use the backend's
     * pre-ranked `logoUrl`/`logoUrlOnDark`/`iconLogoUrl`, and resolve the
     * primary/secondary colors from the derived seed palette (accent/card)
     * with a fallback to the legacy `primaryColor`. The full `suggestedTheme`
     * and `suggestedBrandThemeConfig` are retained so confirm can persist the
     * complete theme, not just a bare color.
     */
    const enterPreviewEdit = useCallback(
      (result: BrandLookupEnrichedResult | null) => {
        const palette = result ? extractLookupPalette(result) : null
        const suggestedTheme = result?.suggestedTheme ?? null
        const suggestedBrandThemeConfig = result?.suggestedBrandThemeConfig ?? null

        const logoUrl = result?.logoUrl ?? null
        const logoUrlOnDark = result?.logoUrlOnDark ?? null
        const iconLogoUrl = result?.iconLogoUrl ?? null
        const primaryColor = normalizeToHex(
          suggestedBrandThemeConfig?.seeds?.accent?.light ?? palette?.accent ?? result?.primaryColor ?? null,
        )
        const secondaryColor =
          suggestedBrandThemeConfig?.seeds?.card?.light ?? palette?.card ?? result?.secondaryColor ?? null
        const domain = result?.domain ?? null

        setEditedLogoUrl(logoUrl)
        setEditedLogoUrlOnDark(logoUrlOnDark)
        setDetectedIconLogoUrl(iconLogoUrl)
        setEditedIconLogoUrl(iconLogoUrl)
        setEditedPrimaryColor(primaryColor)
        setBrandfetchSecondaryColor(secondaryColor)
        setBrandfetchDomain(domain)
        setDetectedTheme(suggestedTheme)
        setDetectedBrandThemeConfig(suggestedBrandThemeConfig)
        setDetectedAccentHex(primaryColor)
        setEditedLogoFile(null)
        setEditedLogoDarkFile(null)
        setBrandSubStep('preview-edit')
        onSubStepChange?.(true)
      },
      [onSubStepChange],
    )

    /** Go back to lookup sub-step, clearing all staged edits. */
    const returnToLookup = useCallback(() => {
      if (stagedObjectUrlRef.current) {
        URL.revokeObjectURL(stagedObjectUrlRef.current)
        stagedObjectUrlRef.current = null
      }
      if (stagedDarkObjectUrlRef.current) {
        URL.revokeObjectURL(stagedDarkObjectUrlRef.current)
        stagedDarkObjectUrlRef.current = null
      }
      setEditedLogoUrl(null)
      setEditedLogoFile(null)
      setEditedLogoUrlOnDark(null)
      setEditedLogoDarkFile(null)
      setEditedIconLogoUrl(null)
      setEditedIconLogoFile(null)
      setDetectedIconLogoUrl(null)
      setEditedPrimaryColor(null)
      setBrandfetchSecondaryColor(null)
      setBrandfetchDomain(null)
      setDetectedTheme(null)
      setDetectedBrandThemeConfig(null)
      setDetectedAccentHex(null)
      reset()
      setBrandSubStep('lookup')
      onSubStepChange?.(false)
    }, [reset, onSubStepChange])

    // When Brandfetch lookup succeeds, automatically advance to preview-edit.
    useEffect(() => {
      if (brand) {
        enterPreviewEdit(brand)
      }
    }, [brand, enterPreviewEdit])

    const LOGO_MAX_BYTES = 5 * 1024 * 1024
    const LOGO_ALLOWED_TYPES = ['image/svg+xml', 'image/png', 'image/jpeg']

    function validateLogoFile(file: File): boolean {
      if (!file.type.startsWith('image/') || !LOGO_ALLOWED_TYPES.includes(file.type)) {
        setLogoError(t('brand.invalidImage'))
        return false
      }
      if (file.size > LOGO_MAX_BYTES) {
        setLogoError(t('brand.imageTooLarge'))
        return false
      }
      setLogoError(null)
      return true
    }

    function handleLogoFileChange(file: File) {
      if (!validateLogoFile(file)) return
      if (stagedObjectUrlRef.current) {
        URL.revokeObjectURL(stagedObjectUrlRef.current)
      }
      const objectUrl = URL.createObjectURL(file)
      stagedObjectUrlRef.current = objectUrl
      setEditedLogoUrl(objectUrl)
      setEditedLogoFile(file)
    }

    function handleLogoDarkFileChange(file: File) {
      if (!validateLogoFile(file)) return
      if (stagedDarkObjectUrlRef.current) {
        URL.revokeObjectURL(stagedDarkObjectUrlRef.current)
      }
      const objectUrl = URL.createObjectURL(file)
      stagedDarkObjectUrlRef.current = objectUrl
      setEditedLogoUrlOnDark(objectUrl)
      setEditedLogoDarkFile(file)
    }

    function handleIconFileChange(file: File) {
      if (!validateLogoFile(file)) return
      const objectUrl = URL.createObjectURL(file)
      setEditedIconLogoUrl(objectUrl)
      setEditedIconLogoFile(file)
    }

    async function handleConfirm() {
      if (savingRef.current) return
      savingRef.current = true
      setIsSaving(true)
      try {
        let resolvedLogoUrl = editedLogoUrl
        let resolvedLogoDarkUrl = editedLogoUrlOnDark
        let resolvedIconLogoUrl = editedIconLogoUrl

        if (editedLogoFile) {
          const { publicUrl } = await filesApi.uploadBrandImage(editedLogoFile, 'light')
          resolvedLogoUrl = publicUrl
        }

        if (editedLogoDarkFile) {
          const { publicUrl } = await filesApi.uploadBrandImage(editedLogoDarkFile, 'dark')
          resolvedLogoDarkUrl = publicUrl
        }

        if (editedIconLogoFile) {
          const { publicUrl } = await filesApi.uploadBrandImage(editedIconLogoFile, 'icon')
          resolvedIconLogoUrl = publicUrl
        }

        // If the user changed the primary color away from the detected accent,
        // override the accent seed so the persisted theme matches their choice.
        // `updateBrandSettings` normalizes hex → HSL for seeds internally.
        let resolvedBrandThemeConfig = detectedBrandThemeConfig
        if (
          resolvedBrandThemeConfig &&
          editedPrimaryColor &&
          editedPrimaryColor !== detectedAccentHex
        ) {
          resolvedBrandThemeConfig = {
            ...resolvedBrandThemeConfig,
            seeds: {
              ...resolvedBrandThemeConfig.seeds,
              accent: {
                light: editedPrimaryColor,
                dark: resolvedBrandThemeConfig.seeds?.accent?.dark ?? editedPrimaryColor,
              },
            },
          }
        }

        // Persist the full detected theme (preset, overrides, fonts, seed
        // palette, and all three logos) so the live theme switches immediately
        // via BrandProvider side-effects. This mirrors Settings › Brand detect.
        const patch: Partial<BrandSettings> = {
          brandDomain: brandfetchDomain,
          brandPrimaryColor: editedPrimaryColor,
          brandSecondaryColor: brandfetchSecondaryColor ?? null,
          brandLogoUrl: resolvedLogoUrl,
          brandLogoUrlOnDark: resolvedLogoDarkUrl,
          brandIconLogoUrl: resolvedIconLogoUrl ?? null,
        }
        if (detectedTheme) {
          patch.themePresetId = detectedTheme.themePresetId as BrandSettings['themePresetId']
          patch.themeOverrides = detectedTheme.themeOverrides
          patch.brandFontFamily = detectedTheme.brandFontFamily as BrandSettings['brandFontFamily']
          patch.brandHeadingFontFamily =
            detectedTheme.brandHeadingFontFamily as BrandSettings['brandHeadingFontFamily']
        }
        if (resolvedBrandThemeConfig) {
          patch.brandThemeConfig = resolvedBrandThemeConfig
        }

        await updateBrandSettings(patch, { immediate: true })
        onNext()
      } catch {
        toast.warning(t('brand.saveWarning'))
        onNext()
      } finally {
        savingRef.current = false
        setIsSaving(false)
      }
    }

    return (
      <div ref={ref} tabIndex={-1} className={cn('flex flex-col gap-6 outline-none', className)}>
        {brandSubStep === 'lookup' && (
          <LookupSubStep
            domain={domain}
            setDomain={setDomain}
            loading={loading}
            error={error}
            onLookup={lookup}
            onManual={() => enterPreviewEdit(null)}
            onSkip={onSkip}
          />
        )}

        {brandSubStep === 'preview-edit' && (
          <PreviewEditSubStep
            editedLogoUrl={editedLogoUrl}
            editedLogoUrlOnDark={editedLogoUrlOnDark}
            editedIconLogoUrl={editedIconLogoUrl}
            editedPrimaryColor={editedPrimaryColor}
            suggestedColor={detectedAccentHex}
            brandfetchDomain={brandfetchDomain}
            orgName={wizardState.orgName}
            isSaving={isSaving}
            logoError={logoError}
            onLogoFileChange={handleLogoFileChange}
            onLogoDarkFileChange={handleLogoDarkFileChange}
            onIconFileChange={handleIconFileChange}
            onColorChange={setEditedPrimaryColor}
            onConfirm={handleConfirm}
            onTryDifferentDomain={returnToLookup}
            onSkip={onSkip}
          />
        )}
      </div>
    )
  },
)
